/**
 * SYSTEM SETTINGS MODULE
 * Connect a Google Sheet and pull it into a dataset (depletions | inventory).
 * Google Identity Services (OAuth, drive.file, code flow → server-stored refresh
 * token) + the Google Picker. Manual "Sync now" and scheduled sync run
 * server-side via the `sheets-sync` edge function.
 *
 * Requires VITE_GOOGLE_CLIENT_ID + VITE_GOOGLE_API_KEY. Until those exist (admin
 * must create the Google Cloud project) the panel shows a "not configured" note.
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  getIntegration, saveIntegration, exchangeGoogleCode, createTemplateSheet, syncNow,
} from "../utils/integrationService.js";
import "./GoogleSheetsPanel.css";

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const API_KEY   = import.meta.env.VITE_GOOGLE_API_KEY;
const SCOPE     = "https://www.googleapis.com/auth/drive.file";        // Picker (file access)
const CONNECT_SCOPE = "openid email " + SCOPE;                          // + identity, so we get the email

const _scripts = {};
function loadScript(src) {
  if (_scripts[src]) return _scripts[src];
  _scripts[src] = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src; s.async = true; s.defer = true;
    s.onload = resolve; s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
  return _scripts[src];
}
const ensureGis  = () => loadScript("https://accounts.google.com/gsi/client");
const ensureGapi = () => loadScript("https://apis.google.com/js/api.js");

// Why a row was thrown away is NOT the same question for every dataset, and this
// is the sentence that tells the customer which column to go and fix. The
// depletions/inventory parsers skip on an unmatched State / Distributor pairing;
// `syncRetailLocations` skips on a missing ZIP or a store that appears twice.
// `sheets-sync` owns the real wording (`RETAIL_SKIP_REASON`, and the default in
// `runSync`), and the stored status line below the button is rendered from it —
// so a hard-coded "no matching SKU / state" here put the green toast in direct
// contradiction with the status line sitting under it. Prefer the reason the
// server sends; fall back to a mirror of it per dataset.
const SKIP_REASON = {
  depletions: "no matching SKU / state",
  inventory: "no matching SKU / state",
  retail_locations: "no ZIP, or a duplicate store in the sheet",
};

function fmtWhen(d) {
  if (!d) return null;
  const dt = new Date(d);
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " " +
    dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

export default function GoogleSheetsPanel({ dataset, label }) {
  const [config, setConfig]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState("");   // "connect" | "pick" | "sync" | ""
  const [error, setError]     = useState(null);
  const [note, setNote]       = useState(null);

  const configured = !!CLIENT_ID && !!API_KEY;

  useEffect(() => {
    let alive = true;
    getIntegration(dataset)
      .then((c) => { if (alive) setConfig(c); })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [dataset]);

  const handleConnect = useCallback(async () => {
    setBusy("connect"); setError(null); setNote(null);
    try {
      await ensureGis();
      const code = await new Promise((resolve, reject) => {
        const client = window.google.accounts.oauth2.initCodeClient({
          client_id: CLIENT_ID, scope: CONNECT_SCOPE, ux_mode: "popup",
          callback: (resp) => { resp.error ? reject(new Error(resp.error)) : resolve(resp.code); },
          error_callback: (err) => reject(new Error(err?.message || err?.type || "Sign-in was cancelled or blocked.")),
        });
        client.requestCode();
      });
      const { email } = await exchangeGoogleCode(code);
      // Mark connected even if Google didn't return an email (so the panel never
      // gets stuck showing "Connect" after a successful authorization).
      setConfig(await saveIntegration(dataset, { connected_email: email || "Google account" }));
    } catch (e) { setError(e.message || "Could not connect."); }
    finally { setBusy(""); }
  }, [dataset]);

  const handleChooseSheet = useCallback(async () => {
    setBusy("pick"); setError(null);
    try {
      await Promise.all([ensureGis(), ensureGapi()]);
      const token = await new Promise((resolve, reject) => {
        const tc = window.google.accounts.oauth2.initTokenClient({
          client_id: CLIENT_ID, scope: SCOPE,
          callback: (resp) => { resp.error ? reject(new Error(resp.error)) : resolve(resp.access_token); },
        });
        tc.requestAccessToken();
      });
      await new Promise((resolve) => window.gapi.load("picker", resolve));
      const picker = new window.google.picker.PickerBuilder()
        .addView(new window.google.picker.DocsView(window.google.picker.ViewId.SPREADSHEETS)
          .setMimeTypes("application/vnd.google-apps.spreadsheet"))
        .setOAuthToken(token)
        .setDeveloperKey(API_KEY)
        .setCallback(async (data) => {
          if (data.action === window.google.picker.Action.PICKED) {
            const doc = data.docs[0];
            try { setConfig(await saveIntegration(dataset, { spreadsheet_id: doc.id, spreadsheet_name: doc.name })); }
            catch (e) { setError(e.message); }
          }
        })
        .build();
      picker.setVisible(true);
    } catch (e) { setError(e.message || "Could not open the picker."); }
    finally { setBusy(""); }
  }, [dataset]);

  const handleCreateTemplate = useCallback(async () => {
    setBusy("create"); setError(null); setNote(null);
    try {
      const r = await createTemplateSheet(dataset);
      setConfig(await getIntegration(dataset));
      window.open(r.url, "_blank", "noopener");
      setNote("Starter sheet created in your Google Drive and connected.");
    } catch (e) { setError(e.message || "Could not create the sheet."); }
    finally { setBusy(""); }
  }, [dataset]);

  const patch = useCallback(async (p) => {
    setError(null);
    try { setConfig(await saveIntegration(dataset, p)); }
    catch (e) { setError(e.message); }
  }, [dataset]);

  const handleSyncNow = useCallback(async () => {
    setBusy("sync"); setError(null); setNote(null);
    try {
      const res = await syncNow(dataset);
      const rows = Number(res.rows) || 0;
      const skipped = Number(res.skipped) || 0;
      setConfig(await getIntegration(dataset));

      const reason = res.skipReason || SKIP_REASON[dataset] || SKIP_REASON.depletions;
      // A sync that DELETED something has to say so in the same breath as the
      // rows it added — the counts are the only place a customer sees it before
      // the data is gone. Inventory removes the rows for states the sheet has
      // stopped listing; retail hides stores that fell out of it. Both are absent
      // from responses that predate the function forwarding them, hence the || 0.
      const removedRows = Number(res.removedRows) || 0;
      const hidden = Number(res.deactivated) || 0;
      const removed =
        dataset === "inventory" && removedRows
          ? ` Removed ${removedRows.toLocaleString()} row${removedRows === 1 ? "" : "s"} for states this sheet no longer lists.`
          : // The other half of the same rule, and the more important one to say
            // out loud: the sheet dropped a state but some rows didn’t resolve,
            // so nothing was removed and the old stock is still being counted.
            dataset === "inventory" && res.keptForSkips
          ? ` Nothing was removed — ${skipped.toLocaleString()} row${skipped === 1 ? "" : "s"} couldn’t be matched, so the rows for states missing from the sheet were kept. Fix those rows and sync again.`
          : dataset === "retail_locations" && hidden
          ? ` ${hidden.toLocaleString()} store${hidden === 1 ? "" : "s"} missing from your sheet ${hidden === 1 ? "is" : "are"} now hidden from the public map.`
          : "";

      setNote(
        `Synced ${rows.toLocaleString()} row${rows === 1 ? "" : "s"} from your sheet` +
        `${skipped ? ` — ${skipped.toLocaleString()} skipped (${reason})` : ""}.${removed}`
      );
    } catch (e) { setError(e.message || "Sync failed."); }
    finally { setBusy(""); }
  }, [dataset]);

  if (!configured) {
    return (
      <div className="gs-panel">
        <div className="gs-title">{label}</div>
        <p className="muted">Google Sheets sync isn’t configured for this workspace yet — the Google Cloud setup is still pending.</p>
      </div>
    );
  }
  if (loading) return <div className="gs-panel"><div className="gs-title">{label}</div><p className="muted">Loading…</p></div>;

  const connected = !!config?.connected_email;
  const hasSheet  = !!config?.spreadsheet_id;

  return (
    <div className="gs-panel">
      <div className="gs-title">{label}</div>
      {error && <p className="error">{error}</p>}
      {note && <p className="gs-ok">{note}</p>}

      {!connected ? (
        <button className="btn" onClick={handleConnect} disabled={busy === "connect"}>
          {busy === "connect" ? "Connecting…" : "Connect Google Sheets"}
        </button>
      ) : (
        <>
          <div className="gs-row"><span className="gs-label">Connected</span><span className="gs-value">{config.connected_email}</span></div>

          {!hasSheet ? (
            <div className="gs-start">
              <div className="gs-actions" style={{ marginTop: 4 }}>
                <button className="btn" onClick={handleCreateTemplate} disabled={busy === "create"}>
                  {busy === "create" ? "Creating…" : "Create a starter sheet"}
                </button>
                <button className="gs-link" onClick={handleChooseSheet} disabled={busy === "pick"}>
                  {busy === "pick" ? "Opening…" : "Choose an existing sheet"}
                </button>
              </div>
              <p className="muted gs-foot">
                “Create a starter sheet” builds a Google Sheet in your Drive, pre-filled with your
                products &amp; states — just enter the numbers. An existing sheet needs columns:{" "}
                {dataset === "inventory"
                  ? "State, Distributor, Flavor, Bottle Size, Package, Current Inventory."
                  : dataset === "retail_locations"
                  ? "Store Name, Address, City, State, ZIP — plus optional Chain, Phone, Website, Products."
                  : "Distributor, State, Flavor, Bottle Size, Package, + month columns like Jan-25."}
              </p>
            </div>
          ) : (
            <>
              <div className="gs-row">
                <span className="gs-label">Sheet</span>
                <span className="gs-value">{config.spreadsheet_name || config.spreadsheet_id}</span>
                <a className="gs-link" href={`https://docs.google.com/spreadsheets/d/${config.spreadsheet_id}/edit`} target="_blank" rel="noreferrer">Open</a>
                <button className="gs-link" onClick={handleChooseSheet} disabled={busy === "pick"}>
                  {busy === "pick" ? "Opening…" : "Change"}
                </button>
              </div>

              <div className="gs-row">
                <span className="gs-label">Tab</span>
                <input
                  className="gs-input"
                  defaultValue={config.sheet_title || ""}
                  placeholder="first tab"
                  onBlur={(e) => { const v = e.target.value.trim(); if (v !== (config.sheet_title || "")) patch({ sheet_title: v || null }); }}
                />
                <span className="muted gs-hint">optional</span>
              </div>

              <div className="gs-row">
                <span className="gs-label">Auto-sync</span>
                <select className="gs-input" value={config.sync_cadence || "manual"} onChange={(e) => patch({ sync_cadence: e.target.value, sync_enabled: e.target.value !== "manual" })}>
                  <option value="manual">Manual only</option>
                  <option value="hourly">Every hour</option>
                  <option value="daily">Every day</option>
                </select>
              </div>

              {/* ⚠️ NO "Stale rows" WINDOW HERE — do not add one back.
                  Migration 0086 shipped an expiry timer for inventory rows whose
                  state had left the sheet, and this panel briefly grew a control
                  for it. Ryan overruled the model on 2026-08-11: the spreadsheet
                  is the source of truth, and the app must not remove anything
                  unless it is gone from the spreadsheet itself. There is no
                  window any more, so there is nothing to configure — the footer
                  below states the rule instead. (The Retail Product Finder's own
                  “How far back to look” is a different setting on a different
                  table and stays.) */}

              <div className="gs-actions">
                <button className="btn" onClick={handleSyncNow} disabled={busy === "sync"}>
                  {busy === "sync" ? "Syncing…" : "Sync now"}
                </button>
                {config.last_sync_at && (() => {
                  // Three states, not two (item 332). `last_sync_status` is "ok"
                  // whenever the run COMPLETED, so a sync that skipped 90% of the
                  // sheet (item 26) or wrote rows with no matching SKU (item 104)
                  // used to render its own "⚠ …" text next to a green ✓ — a
                  // customer scanning for a red flag saw a tick.
                  // The sync itself already decides what deserves attention: it
                  // prefixes exactly those messages with ⚠ and deliberately leaves
                  // the milder "(N skipped — …)" form unmarked. So that marker is
                  // the signal, and no glyph is prepended in the warning case —
                  // the message carries its own.
                  const msg = config.last_sync_message || "";
                  const state = config.last_sync_status === "error" ? "error"
                    : msg.includes("⚠") ? "warn"
                    : "ok";
                  return (
                    <span className={`gs-status gs-status-${state}`}>
                      {state === "error" ? "⚠ " : state === "ok" ? "✓ " : ""}
                      {msg || config.last_sync_status} · {fmtWhen(config.last_sync_at)}
                    </span>
                  );
                })()}
              </div>
              {/* What a sync does is NOT the same for all three datasets, and this
                  is the sentence a customer reads to decide whether one is safe.
                  It said "replaces this dataset with the sheet's current contents"
                  for every dataset; no path has done that since APP-612 scoped
                  depletions to the months it mentions and APP-52 scoped inventory
                  to the states it claims. Keep each branch to what THIS sync
                  rewrites and what it leaves alone — the status line above already
                  reports the per-run numbers, and the retail cutoff is explained by
                  the control directly below this panel. */}
              <p className="muted gs-foot">
                {dataset === "inventory"
                  ? "Your sheet is the source of truth: what it says is what you get. Each sync rewrites the states your sheet lists, and a state you take out of the sheet comes off after the next sync that reads the whole file cleanly — so remove a state’s rows there and its stock leaves the app. Only rows an earlier sync wrote are touched (same matching as a file import); anything typed by hand or brought in by a file import is left alone. If any row can’t be matched, nothing is removed that run — the status above says so, and names what to fix."
                  : dataset === "retail_locations"
                  ? "Each sync adds and updates the stores your sheet lists, matched on store name + ZIP, and never touches a store entered by hand. A store the sheet drops comes off the map once the cutoff below has passed."
                  : "Each sync rewrites only the months and states your sheet lists (same matching as a file import). A month or state it doesn’t mention keeps what it already had — depletions are history, so nothing expires."}
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}
